import { Facebook, Globe, Instagram, Share2, Youtube } from "lucide-react";

const NETWORKS = [
  { key: "website", label: "Website", Icon: Globe, color: "#1A2E26" },
  { key: "youtube", label: "YouTube", Icon: Youtube, color: "#E62117" },
  { key: "facebook", label: "Facebook", Icon: Facebook, color: "#1877F2" },
  { key: "instagram", label: "Instagram", Icon: Instagram, color: "#C13584" },
];

export const emailHref = (raw) => {
  const v = (raw || "").trim();
  return v ? (v.startsWith("mailto:") ? v : `mailto:${v}`) : "";
};

const toHref = (key, raw) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (key === "email") return v.startsWith("mailto:") ? v : `mailto:${v}`;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const toHandle = (key, raw) => {
  const v = (raw || "").trim().replace(/^mailto:/i, "");
  if (key === "email") return v;
  if (key === "website") return v.replace(/^(?!https?:\/\/)/i, "https://").replace(/\/+$/, "");
  const stripped = v.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
  const last = stripped.split("/").pop() || stripped;
  return last.startsWith("@") ? last : `@${last}`;
};

export const socialItems = (content) =>
  NETWORKS.map((n) => ({ ...n, href: toHref(n.key, content?.[`social_${n.key}`]), handle: toHandle(n.key, content?.[`social_${n.key}`]) }))
    .filter((n) => n.href && content?.[`social_${n.key}_visible`] !== false);

const Header = ({ title }) => (
  <div className="flex items-center gap-2 text-accent mb-3"><Share2 size={16} /><span className="text-xs uppercase tracking-widest font-medium">{title}</span></div>
);

const IconsStyle = ({ items }) => (
  <div className="flex flex-wrap gap-2.5">
    {items.map(({ key, label, Icon, color, href, handle }) => (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer" data-testid={`social-${key}`} title={`${label}: ${handle}`}
        className="group flex items-center gap-2 rounded-full border border-border bg-white/80 pl-1.5 pr-4 py-1.5 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] duration-300">
        <span className="grid place-items-center w-8 h-8 rounded-full text-white" style={{ backgroundColor: color }}><Icon size={15} /></span>
        <span className="text-xs font-medium text-foreground truncate max-w-[230px]">{handle}</span>
      </a>
    ))}
  </div>
);

const ListStyle = ({ items }) => (
  <ul className="divide-y divide-border/70">
    {items.map(({ key, label, Icon, color, href, handle }) => (
      <li key={key}>
        <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`social-${key}`}
          className="flex items-center gap-3 py-2.5 group hover:text-accent transition-colors">
          <span className="grid place-items-center w-7 h-7 rounded-lg shrink-0" style={{ color, backgroundColor: `${color}14` }}><Icon size={15} /></span>
          <span className="text-xs uppercase tracking-widest text-muted-foreground w-20 shrink-0">{label}</span>
          <span className="text-sm font-medium truncate group-hover:underline decoration-accent/40">{handle}</span>
        </a>
      </li>
    ))}
  </ul>
);

const CardsStyle = ({ items }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
    {items.map(({ key, label, Icon, color, href, handle }) => (
      <a key={key} href={href} target="_blank" rel="noopener noreferrer" data-testid={`social-${key}`}
        className="rounded-xl border border-border bg-white/80 p-3 flex flex-col gap-2 hover:border-accent/50 hover:-translate-y-0.5 transition-[transform,border-color] duration-300">
        <span className="grid place-items-center w-9 h-9 rounded-lg text-white" style={{ backgroundColor: color }}><Icon size={17} /></span>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className="text-xs font-medium break-all leading-snug -mt-1.5">{handle}</span>
      </a>
    ))}
  </div>
);

export const SocialLinks = ({ content }) => {
  const items = socialItems(content);
  if (!items.length) return null;
  const style = content?.social_style || "icons";
  return (
    <div data-testid="announcement-social-block" className="rounded-2xl border border-border bg-background/60 p-5">
      <Header title="Connect With Us" />
      {style === "list" ? <ListStyle items={items} /> : style === "cards" ? <CardsStyle items={items} /> : <IconsStyle items={items} />}
    </div>
  );
};
